import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CatalogService, InventoryRecallCampaign, RecallControlRoomSummary } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-recalls',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('catalog.recall.campaigns.title')" [description]="t('catalog.recall.campaigns.description')">
      <ain-inventory-page-hint pageKey="recalls" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        @if (controlRoom()) {
          <section class="control-room">
            <h4>{{ t('catalog.recall.controlRoom.title') }}</h4>
            <div class="stats">
              <article><strong>{{ t('catalog.tracking.status') }} Draft</strong><span>{{ controlRoom()!.draftCount }}</span></article>
              <article><strong>PendingApproval</strong><span>{{ controlRoom()!.pendingApprovalCount }}</span></article>
              <article><strong>Executing</strong><span>{{ controlRoom()!.executingCount }}</span></article>
              <article><strong>Completed</strong><span>{{ controlRoom()!.completedCount }}</span></article>
            </div>
          </section>
        }

        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('catalog.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelectedDetail()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>

          <div class="filter-toggle">
            <button pButton type="button" [label]="showFilters() ? t('catalog.filter.hide') : t('catalog.filter.show')" severity="secondary" (click)="showFilters.set(!showFilters())"></button>
          </div>

          @if (showFilters()) {
            <div class="filters">
              <label>
                <span>{{ t('catalog.tracking.status') }}</span>
                <p-select
                  [options]="statusOptions"
                  [(ngModel)]="filters.status"
                  optionLabel="label"
                  optionValue="value"
                  [showClear]="true"
                  appendTo="body"
                  (ngModelChange)="load()" />
              </label>
            </div>
          }

          <p-table
            [value]="rows()"
            dataKey="id"
            [paginator]="true"
            [rows]="15"
            [selection]="selectedRows()"
            (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('catalog.recall.campaignNumber') }}</th>
                <th>{{ t('catalog.recall.name') }}</th>
                <th>{{ t('catalog.recall.reasonCode') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.recall.totalLines') }}</th>
                <th>{{ t('catalog.recall.quarantinedLines') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.campaignNumber }}</button></td>
                <td>{{ row.name }}</td>
                <td>{{ row.reasonCode }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.totalLines }}</td>
                <td>{{ row.quarantinedLines }}</td>
              </tr>
            </ng-template>
          </p-table>

          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .control-room { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .control-room h4 { margin: 0 0 8px; }
    .stats { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); }
    .stats article { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px; display: grid; gap: 4px; }
    .stats strong { font-size: 11px; color: #64748b; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filter-toggle { display: flex; justify-content: flex-start; }
    .filters { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
    .filters label { display: grid; gap: 6px; }
    .filters span { font-size: 12px; color: #475569; font-weight: 600; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { margin: 0; color: #b91c1c; }
  `]
})
export class InventoryRecallsComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly showFilters = signal(true);
  readonly rows = signal<InventoryRecallCampaign[]>([]);
  readonly controlRoom = signal<RecallControlRoomSummary | null>(null);
  readonly selectedRows = signal<InventoryRecallCampaign[]>([]);
  readonly filters = { status: '' };

  readonly statusOptions = [
    { label: '—', value: '' },
    { label: 'Draft', value: 'Draft' },
    { label: 'PendingApproval', value: 'PendingApproval' },
    { label: 'Approved', value: 'Approved' },
    { label: 'Executing', value: 'Executing' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.load();
    this.service.getRecallControlRoom().subscribe({ next: summary => this.controlRoom.set(summary) });
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.service.listRecallCampaigns(this.filters.status || undefined).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.error.set(this.t('catalog.recall.campaigns.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  openCreate(): void {
    void this.router.navigate(['/inventory/recalls/new']);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/inventory/recalls', id]);
  }

  openSelectedDetail(): void {
    const row = this.selectedRows()[0];
    if (row) this.openDetail(row.id);
  }

  onSelectionChange(rows: InventoryRecallCampaign[]): void {
    this.selectedRows.set(rows ?? []);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
